import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardHeader, CardBody } from "../../design-system/Card";
import { Badge, type BadgeTone } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { StatCard } from "../../design-system/StatCard";
import { ProgressBar, type ProgressTone } from "../../design-system/ProgressBar";
import { Icon, type IconName } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import type {
  ProductDevelopmentOverviewDto, PdCandidateDto, PdVerdict, OpenInvestigationInput, SimulationResultDto,
} from "../../ipc/types";
import { ProductImageStudio } from "./ProductImageStudio";
import { ProductDescriptionStudio } from "./ProductDescriptionStudio";
import { PriceCalculator } from "./PriceCalculator";
import { toCsv, suggestCsvName } from "../../lib/csv";
import "../dashboard/dashboard.css";
import "./product-development.css";

const VERDICT: Record<PdVerdict, { label: string; tone: BadgeTone }> = {
  no_sell: { label: "No vender", tone: "danger" },
  high_risk: { label: "Riesgo alto", tone: "danger" },
  medium_risk: { label: "Riesgo medio", tone: "warning" },
  interesting: { label: "Interesante", tone: "info" },
  recommended: { label: "Recomendado", tone: "brand" },
  star: { label: "Producto estrella", tone: "success" },
  exceptional: { label: "Oportunidad excepcional", tone: "success" },
};

/** Color de barra según el valor del índice/probabilidad (0..100). */
export function toneForValue(value: number): ProgressTone {
  if (value >= 80) return "success";
  if (value >= 60) return "brand";
  if (value >= 40) return "info";
  return "danger";
}

const EMPTY_FORM: OpenInvestigationInput = {
  name: "", category: "", demand: 70, competition: 40, marginPct: 40, aiConfidence: 75, leadTimeDays: 30,
};

function VerdictRing({ probability }: { probability: number }): JSX.Element {
  const r = 40;
  const circ = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, probability)) / 100) * circ;
  return (
    <div className="pd-detail__ring">
      <svg width="92" height="92" viewBox="0 0 92 92" aria-hidden="true">
        <circle cx="46" cy="46" r={r} fill="none" stroke="var(--bg-overlay)" strokeWidth="8" />
        <circle cx="46" cy="46" r={r} fill="none" stroke="var(--ml-yellow)" strokeWidth="8" strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} />
      </svg>
      <span className="pd-detail__ringval tnum">{probability}</span>
    </div>
  );
}

function Slider({ label, value, min, max, suffix, onChange }: {
  label: string; value: number; min: number; max: number; suffix?: string; onChange: (v: number) => void;
}): JSX.Element {
  return (
    <div className="pd-field">
      <span className="pd-field__label">{label}<b className="tnum">{value}{suffix ?? ""}</b></span>
      <input className="pd-range" type="range" min={min} max={max} value={value} aria-label={label}
        onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}

function NewInvestigationForm({ onCreate, onCancel, busy, initial }: {
  onCreate: (input: OpenInvestigationInput) => void; onCancel: () => void; busy: boolean; initial?: OpenInvestigationInput | undefined;
}): JSX.Element {
  const [form, setForm] = useState<OpenInvestigationInput>(initial ?? EMPTY_FORM);
  const set = <K extends keyof OpenInvestigationInput>(key: K, v: OpenInvestigationInput[K]): void => setForm((f) => ({ ...f, [key]: v }));
  const valid = form.name.trim().length >= 2 && form.category.trim().length >= 2;
  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader title="Nueva investigación" subtitle="Ingresa el candidato y sus señales; la IA lo evalúa al instante" />
      <CardBody>
        <div className="pd-form">
          <div className="pd-field pd-form__full">
            <span className="pd-field__label">Producto candidato</span>
            <input className="pd-input" type="text" value={form.name} placeholder="Ej: Lámpara LED de escritorio plegable"
              aria-label="Producto candidato" onChange={(e) => set("name", e.target.value)} />
          </div>
          <div className="pd-field pd-form__full">
            <span className="pd-field__label">Categoría / nicho</span>
            <input className="pd-input" type="text" value={form.category} placeholder="Ej: Hogar / Iluminación / Escritorio"
              aria-label="Categoría" onChange={(e) => set("category", e.target.value)} />
          </div>
          <Slider label="Demanda" value={form.demand} min={0} max={100} onChange={(v) => set("demand", v)} />
          <Slider label="Competencia" value={form.competition} min={0} max={100} onChange={(v) => set("competition", v)} />
          <Slider label="Margen" value={form.marginPct} min={0} max={100} suffix="%" onChange={(v) => set("marginPct", v)} />
          <Slider label="Confianza IA" value={form.aiConfidence} min={0} max={100} onChange={(v) => set("aiConfidence", v)} />
          <Slider label="Lead time (días)" value={form.leadTimeDays} min={0} max={180} onChange={(v) => set("leadTimeDays", v)} />
        </div>
        <div className="pd-actions">
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button size="sm" icon="check" disabled={!valid || busy} onClick={() => onCreate(form)}>
            {busy ? "Evaluando…" : "Evaluar candidato"}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function Simulator({ candidate }: { candidate: PdCandidateDto }): JSX.Element {
  const client = useIpc();
  const [price, setPrice] = useState(0);
  const [ads, setAds] = useState(0);
  const [result, setResult] = useState<SimulationResultDto | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (): Promise<void> => {
    setBusy(true);
    const res = await client.simulateInvestigation({ candidateId: candidate.id, priceDeltaPct: price, adSpendDeltaPct: ads });
    setBusy(false);
    if (res.ok) setResult(res.value);
  };

  return (
    <div className="pd-sim">
      <div className="section-title">Simulador de escenario</div>
      <div className="pd-form" style={{ marginTop: "var(--sp-3)" }}>
        <Slider label="Δ Precio" value={price} min={-50} max={50} suffix="%" onChange={setPrice} />
        <Slider label="Δ Publicidad" value={ads} min={0} max={100} suffix="%" onChange={setAds} />
      </div>
      <div className="pd-actions">
        <Button size="sm" variant="secondary" icon="pulse" onClick={run} disabled={busy}>
          {busy ? "Simulando…" : "Simular"}
        </Button>
      </div>
      {result ? (
        <div className="pd-sim__result">
          <span className="pd-sim__big tnum">{result.resultProbability}%</span>
          <Badge tone={result.delta >= 0 ? "success" : "danger"} dot>
            {result.delta >= 0 ? "+" : ""}{result.delta} pts vs. base ({result.baseProbability}%)
          </Badge>
        </div>
      ) : null}
    </div>
  );
}

function CandidateDetail({ candidate }: { candidate: PdCandidateDto }): JSX.Element {
  const v = VERDICT[candidate.verdict];
  return (
    <div className="grid">
      <div className="pd-detail__verdict">
        <VerdictRing probability={candidate.successProbability} />
        <div>
          <div className="pd-detail__vtitle">Veredicto de la matriz</div>
          <div className="pd-detail__vname">{v.label}</div>
          <div className="pd-detail__vmeta">
            {candidate.successProbability}% de probabilidad · margen {candidate.marginPct}% · {candidate.monthlyUnits} u/mes est.
          </div>
        </div>
      </div>

      <Card>
        <CardHeader title="Scoreboard · 13 índices" subtitle={candidate.name} action={<Badge tone={v.tone} dot>{v.label}</Badge>} />
        <CardBody>
          {candidate.indices.map((idx) => (
            <div className="pd-index" key={idx.key}>
              <span className="pd-index__label">{idx.label}</span>
              <ProgressBar value={idx.value} tone={toneForValue(idx.value)} ariaLabel={idx.label} />
              <span className="pd-index__val tnum">{idx.value}</span>
            </div>
          ))}
          <Simulator candidate={candidate} />
        </CardBody>
      </Card>
    </div>
  );
}

export function ProductDevelopmentScreen(): JSX.Element {
  const client = useIpc();
  const { state, reload } = useIpcQuery<ProductDevelopmentOverviewDto>((c) => c.getProductDevelopmentOverview(), []);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState<string | null>(null);
  // Puente desde Tendencias ML (Fase 9): si llega un "seed", abre el formulario ya rellenado (una vez).
  const location = useLocation();
  const [seed, setSeed] = useState<OpenInvestigationInput | undefined>(undefined);
  const seededRef = useRef(false);
  useEffect(() => {
    const s = (location.state as { pdSeed?: OpenInvestigationInput } | null)?.pdSeed;
    if (s && !seededRef.current) {
      seededRef.current = true;
      setSeed(s);
      setShowForm(true);
      window.history.replaceState({}, ""); // limpia el state para que no reaparezca al volver
    }
  }, [location.state]);

  if (state.status === "loading") return <LoadingState />;
  if (state.status === "error") return <ErrorState message={state.error.message} onRetry={reload} />;

  const data = state.data;
  const selected = data.candidates.find((c) => c.id === selectedId) ?? data.candidates[0];

  const create = async (input: OpenInvestigationInput): Promise<void> => {
    setCreating(true);
    const res = await client.openInvestigation(input);
    setCreating(false);
    if (res.ok) {
      setSelectedId(res.value.id);
      setShowForm(false);
      reload();
    }
  };

  const exportCandidates = async (): Promise<void> => {
    if (data.candidates.length === 0) return;
    setExporting(true); setExportMsg(null);
    const rows = data.candidates.map((c) => [c.name, c.category, VERDICT[c.verdict].label, `${c.successProbability}%`, `${c.marginPct}%`, `${c.monthlyUnits}`]);
    const csv = toCsv(["Producto", "Categoría", "Veredicto", "Probabilidad", "Margen", "U/mes est."], rows);
    const res = await client.saveExport(suggestCsvName("candidatos-producto"), csv);
    setExporting(false);
    if (!res.ok) { if (res.error.code !== "cancelled") setExportMsg(res.error.message); return; }
    setExportMsg(`Candidatos exportados a: ${res.value.path}`);
  };

  return (
    <div>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}>
              <Icon name="flask" size={22} />
            </div>
            <div>
              <h1 className="pagehead__title">Desarrollo de Producto</h1>
              <p className="pagehead__sub">Departamento de I+D asistido por IA: descubre productos rentables antes de invertir.</p>
            </div>
          </div>
          <div className="row">
            <Button variant="secondary" size="sm" icon="analytics" disabled={exporting || data.candidates.length === 0} onClick={exportCandidates}>{exporting ? "Exportando…" : "Exportar"}</Button>
            <Button size="sm" icon="flask" onClick={() => setShowForm((s) => !s)}>Nueva investigación</Button>
          </div>
        </div>
      </div>

      {exportMsg ? <div className="callout" style={{ marginBottom: "var(--sp-4)" }}><div className="bar" /><div className="body">{exportMsg}</div></div> : null}

      <div className="kpis" style={{ marginTop: 0 }}>
        {data.kpis.map((k) => (
          <StatCard key={k.key} label={k.label} value={k.value} icon={k.icon as IconName} accent={k.accent} deltaPct={k.deltaPct} />
        ))}
      </div>

      {showForm ? <div style={{ marginTop: "var(--sp-5)" }}><NewInvestigationForm onCreate={create} onCancel={() => { setShowForm(false); setSeed(undefined); }} busy={creating} initial={seed} /></div> : null}

      <div className="pd-cols">
        <Card>
          <CardHeader title="Ranking de candidatos" subtitle="Ordenados por probabilidad de éxito" action={<Badge tone="brand" dot>{data.candidates.length} en estudio</Badge>} />
          <div className="card__body" style={{ padding: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Producto candidato</th>
                  <th>Veredicto</th>
                  <th>Probabilidad</th>
                </tr>
              </thead>
              <tbody>
                {data.candidates.map((c) => {
                  const active = selected && c.id === selected.id;
                  return (
                    <tr
                      key={c.id}
                      className={`pd-row${active ? " pd-row--active" : ""}`}
                      onClick={() => setSelectedId(c.id)}
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedId(c.id); } }}
                      aria-label={`Ver ${c.name}`}
                    >
                      <td>
                        <div className="strong">{c.name}</div>
                        <div className="muted" style={{ fontSize: 12 }}>{c.category}</div>
                      </td>
                      <td><Badge tone={VERDICT[c.verdict].tone} dot>{VERDICT[c.verdict].label}</Badge></td>
                      <td>
                        <div className="pd-prob">
                          <div className="pd-prob__bar"><ProgressBar value={c.successProbability} tone={toneForValue(c.successProbability)} ariaLabel={`Probabilidad ${c.name}`} /></div>
                          <span className="pd-prob__val tnum">{c.successProbability}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {selected ? <CandidateDetail candidate={selected} /> : null}
      </div>

      <PriceCalculator />

      <ProductDescriptionStudio />

      <ProductImageStudio />
    </div>
  );
}
